import { useState, useEffect } from "react";
import { X, ExternalLink, Download, Loader2 } from "lucide-react";
import StatusBadge from "./StatusBadge.jsx";
import { formatDateTime } from "../../utils/dateUtils.js";

const SUMMARY_MAX = 280;

const JobDetailsModal = ({ job, onClose, downloadingId, onDownload }) => {
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  useEffect(() => { setSummaryExpanded(false); }, [job]);

  if (!job) return null;

  return (
    <div
      className="fixed inset-0 z-9999 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl rounded-2xl bg-[#f6f8fb] shadow-2xl flex flex-col"
        style={{ maxHeight: "min(85vh, 760px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-slate-100 shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-900 truncate">
              {job.document_title || "Generation Details"}
            </h2>
            {job.repository && (
              <p className="text-sm text-slate-500 mt-0.5 flex flex-wrap items-center gap-1.5">
                <span className="truncate max-w-xs">{job.repository}</span>
                {job.source_version && (
                  <span className="font-mono text-xs bg-slate-100 rounded px-1.5 py-0.5 whitespace-nowrap">
                    {job.source_version} → {job.target_version}
                  </span>
                )}
                {job.release_tag && (
                  <span className="font-mono text-xs bg-slate-100 rounded px-1.5 py-0.5 whitespace-nowrap">
                    {job.release_tag}
                  </span>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-lg border-0 bg-white text-slate-500 hover:text-slate-800 hover:bg-slate-100 cursor-pointer shadow-sm"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-6 pb-6">
          {job.status === "completed" ? (
            <div className="flex flex-col gap-5 pt-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 py-4 border-b border-slate-100">
                {[
                  { label: "Work Items", value: job.total_work_items ?? 0 },
                  { label: "Diffs",      value: job.total_diffs ?? 0 },
                  { label: "Generated",  value: formatDateTime(job.completed_at), small: true },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="text-[11px] uppercase font-black text-slate-400 mb-1">{s.label}</p>
                    <p className={`font-bold text-slate-700 ${s.small ? "text-xs" : "text-lg"}`}>{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Executive summary */}
              {job.executive_summary && (
                <div className="bg-white rounded-xl border border-slate-100 p-4">
                  <p className="text-[11px] uppercase font-black text-slate-400 mb-3">Executive Summary</p>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {summaryExpanded || job.executive_summary.length <= SUMMARY_MAX
                      ? job.executive_summary
                      : job.executive_summary.slice(0, SUMMARY_MAX).trimEnd() + "…"}
                  </p>
                  {job.executive_summary.length > SUMMARY_MAX && (
                    <button
                      type="button"
                      onClick={() => setSummaryExpanded((v) => !v)}
                      className="mt-2 text-xs font-semibold text-blue-600 hover:text-blue-800 border-0 bg-transparent cursor-pointer p-0"
                    >
                      {summaryExpanded ? "Show less" : "Read more"}
                    </button>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <a
                  href={job.published_url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="h-9 px-5 flex items-center gap-2 bg-white text-slate-700 border border-slate-200 font-semibold rounded-lg hover:bg-slate-50 transition-all text-xs no-underline shadow-sm cursor-pointer"
                >
                  <ExternalLink size={14} /> View Document
                </a>
                <button
                  type="button"
                  onClick={() => onDownload(job)}
                  disabled={!!downloadingId || !job.published_url}
                  className="h-9 px-5 flex items-center gap-2 bg-[#1d4ed8] text-white font-semibold rounded-lg hover:bg-[#1e40af] transition-all text-xs disabled:opacity-50 border-0 cursor-pointer shadow-sm"
                >
                  {downloadingId === job.job_id
                    ? <Loader2 size={14} className="animate-spin" />
                    : <Download size={14} />}
                  {downloadingId === job.job_id ? "Downloading…" : "Download File"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-10 text-slate-400">
              <StatusBadge status={job.status} />
              {job.status === "failed" && job.error_message && (
                <p className="text-sm text-red-500 text-center max-w-md">{job.error_message}</p>
              )}
              {(job.status === "queued" || job.status === "running") && (
                <p className="text-sm">Document generation is still in progress.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobDetailsModal;
